import { UserRoundX } from '@/lib/icons';
import { ReportPageLayout } from '@/components/reports';
import { reportsService } from '@/services/reports-service';
import type { ColumnDef, FilterConfig, ReportFilters } from '@/types/reports';
import { formatDisplayDateTime } from '@/lib/date-utils';

interface DeletedUser {
  id: string;
  attributes: {
    original_id: string;
    first_name: string;
    last_name: string;
    email: string;
    deleted_by_email?: string;
    deleted_on: string;
  };
}

const columns: ColumnDef<DeletedUser>[] = [
  {
    key: 'original_id',
    header: 'Portal Access ID',
    render: (item) => item.attributes.original_id,
  },
  {
    key: 'name',
    header: 'Name',
    render: (item) => `${item.attributes.first_name} ${item.attributes.last_name}`,
  },
  {
    key: 'email',
    header: 'Email',
    render: (item) => item.attributes.email,
  },
  {
    key: 'deleted_by',
    header: 'Deleted By',
    render: (item) => item.attributes.deleted_by_email || 'System',
  },
  {
    key: 'deleted_on',
    header: 'Deleted On',
    render: (item) => formatDisplayDateTime(item.attributes.deleted_on),
  },
];

const filterConfig: FilterConfig = {
  fields: [
    { key: 'email_domain', type: 'text', label: 'Email Domain', placeholder: 'e.g., prosci.com' },
    { key: 'from_date', type: 'date', label: 'Deleted From', placeholder: 'Select date' },
    { key: 'to_date', type: 'date', label: 'Deleted To', placeholder: 'Select date' },
  ],
  hasFiltersCheck: (filters: ReportFilters) =>
    Boolean(filters.email_domain || filters.from_date || filters.to_date),
};

export function DeletedUsersReport() {
  return (
    <ReportPageLayout<DeletedUser>
      title="Deleted Users Report"
      description="Track users that have been deleted from your organization."
      breadcrumbLabel="Deleted Users"
      itemLabel="deleted users"
      queryKey="deleted-users"
      fetchData={reportsService.getDeletedUsers}
      downloadData={reportsService.downloadDeletedUsers}
      filterConfig={filterConfig}
      columns={columns}
      sortData={(data) =>
        [...data].sort((a, b) => {
          const dateA = a.attributes.deleted_on ? new Date(a.attributes.deleted_on).getTime() : 0;
          const dateB = b.attributes.deleted_on ? new Date(b.attributes.deleted_on).getTime() : 0;
          return dateB - dateA;
        })
      }
      getRowKey={(item) => item.id}
      accessCheck={{
        requiredLevels: ['super', 'admin', 'data'],
        deniedMessage: 'You do not have permission to view this report.',
      }}
      emptyIcon={<UserRoundX />}
      emptyTitle="No deleted users found"
      emptyDescription="Try adjusting your filters to find deleted users."
    />
  );
}
